import iconPercentage from "../../../../design/assests/images/pdf_icons/porcentaje.png"
import html2canvas from "html2canvas"
import { numberFormatter } from "../../../../utils/numbers.utils"
import {
  convertWeekEstimnatedtoDateEstimated,
  getProyectionInBlocksByWeeks,
} from "../../../../utils/clientReport.utils"
import { formatOnlyMonth } from "../../../../utils/date.utils"
import { PERIOD_LABELS } from "../../../../consts/modules/clientReport.consts"

export const buildPointProgressSection = async (
  doc,
  sprintData,
  y,
  data,
  refsToCapture,
  period,
  extradata
) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const sideMargin = 10
  const bottomMargin = 25
  const usableWidth = pageWidth - sideMargin * 2
  const columnWidth = usableWidth / 3

  const iconSize = 6
  const spacing = 2
  const iconOffsetY = 4.5

  const labelColor = "#737E8D"
  const valueColor = "#24292F"
  const titleColor = "#24292F"

  const isDataEnough = data?.totalCompletedPoints && data?.totalPointsToComplete

  const pessimisticProjection = extradata?.semanasPesimistas ?? null
  const optimisticProjection = extradata?.semanasOptimistas ?? null
  const nominalProjection = extradata?.semanasNominales ?? null

  const endDateReference = extradata?.blockEndDate || null

  const averagePoints =
    extradata?.averagePointsPerBlock ?? data?.averagePointsPerSprint ?? null

  const nominalProjectionInDate =
    nominalProjection && endDateReference ?
      convertWeekEstimnatedtoDateEstimated(
        endDateReference,
        nominalProjection,
        extradata?.holidays || []
      )
    : null

  const nominalProjectionFormatted =
    nominalProjectionInDate ? formatOnlyMonth(nominalProjectionInDate) : null

  // page break
  const estimatedHeight = 60

  if (y + estimatedHeight > pageHeight - bottomMargin) {
    doc.addPage()
    y = 20
  }

  doc.setFont("Cairo-SemiBold", "normal")
  doc.setFontSize(9)
  doc.setTextColor(titleColor)

  doc.addImage(
    iconPercentage,
    "PNG",
    sideMargin,
    y - iconOffsetY,
    iconSize,
    iconSize
  )

  doc.text("Progreso del alcance", sideMargin + iconSize + spacing, y)

  y += 5

  const contentTopY = y

  //
  // COLUMNA 1 - CHART
  //

  function createLightModeClone(element) {
    const clone = element.cloneNode(true)

    let graphColor = "#0A5587"
    const path = clone.querySelector("path")

    if (path) {
      const pathColor = path.getAttribute("fill")

      if (pathColor && !pathColor.includes("var")) {
        graphColor = pathColor
      } else if (pathColor && pathColor.includes("--cyan-600")) {
        graphColor = "#0A5587"
      }
    }

    function fixLightMode(node) {
      if (!node.style || !node.tagName) return

      const tagName = node.tagName.toLowerCase()
      const className = node.className || ""

      if (tagName === "div") {
        node.style.backgroundColor = "#ffffff"
      }

      if (tagName === "text" || tagName === "tspan") {
        node.style.fill = "#24292F"
      }

      if (tagName === "path") {
        const nameAttr = node.getAttribute("name") || ""

        if (nameAttr === "Avance actual") {
          node.style.fill = graphColor
        }

        if (nameAttr === "Puntos faltantes") {
          node.style.fill = "#DCE1E8"
        }

        node.style.stroke = "#ffffff"
        node.style.setProperty("stroke", "#ffffff", "important")
        node.style.strokeWidth = "2"
        node.setAttribute("stroke-width", "2")
      }

      if (
        tagName === "div" &&
        className &&
        (className.includes("border-round-md") ||
          (node.style.width === "1rem" && node.style.height === "1rem"))
      ) {
        node.style.backgroundColor = graphColor
        node.style.background = graphColor
      }

      if (node.textContent && node.textContent.includes("Avance actual")) {
        node.style.color = "#24292F"
        node.style.opacity = "1"
      }

      if (node.children) {
        Array.from(node.children).forEach((child) => fixLightMode(child))
      }
    }

    fixLightMode(clone)

    return clone
  }

  const graphDiv =
    refsToCapture.POINT_PROGRESS || refsToCapture["GROUPED-POINT_PROGRESS"]

  if (graphDiv) {
    const clone = createLightModeClone(graphDiv)

    clone.style.position = "absolute"
    clone.style.left = "-9999px"

    document.body.appendChild(clone)

    const canvas = await html2canvas(clone, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    })

    document.body.removeChild(clone)

    const imgData = canvas.toDataURL("image/png")

    const maxWidth = columnWidth - 10
    const maxHeight = 42

    const scale = Math.min(maxWidth / canvas.width, maxHeight / canvas.height)

    const imgWidth = canvas.width * scale
    const imgHeight = canvas.height * scale

    const centerX = sideMargin + columnWidth / 2 - imgWidth / 2

    doc.addImage(imgData, "PNG", centerX, contentTopY, imgWidth, imgHeight)
  }

  //
  // COLUMNA 2
  //

  const reference = parseFloat(data?.prevAveragePointsPerSprint)
  const averagePointsNumeric = parseFloat(averagePoints)
  const value = isNaN(averagePointsNumeric) ? 0 : averagePointsNumeric
  const offset = 1

  const showTendency =
    !isNaN(reference) &&
    reference > 0 &&
    value > 0 &&
    !(reference <= value + offset && reference >= value - offset)
  const isPositive = value > reference

  const col2X = sideMargin + columnWidth
  let col2Y = contentTopY + 13

  doc.setFont("Mulish-Bold", "normal")
  doc.setFontSize(7.5)
  doc.setTextColor(labelColor)
  doc.text("Avance en puntos", col2X, col2Y)

  col2Y += 6

  doc.setFont("Cairo-SemiBold", "normal")
  doc.setFontSize(12)
  doc.setTextColor(valueColor)

  if (isDataEnough) {
    const mainValue = numberFormatter(data?.totalCompletedPoints)
    const secondaryValue = `/${numberFormatter(data?.totalPointsToComplete)} pts dev`

    doc.setFont("Cairo-SemiBold", "normal")
    doc.setFontSize(12)

    const mainWidth = doc.getTextWidth(mainValue)

    doc.text(mainValue, col2X, col2Y)

    doc.setFontSize(9)
    doc.text(secondaryValue, col2X + mainWidth, col2Y)
  } else {
    doc.text("-", col2X, col2Y)
  }

  col2Y += 7

  doc.setFont("Mulish-Bold", "normal")
  doc.setFontSize(7.5)
  doc.setTextColor(labelColor)
  doc.text("Proyección de bloques restantes", col2X, col2Y)

  col2Y += 6

  doc.setFont("Cairo-SemiBold", "normal")
  doc.setFontSize(12)
  doc.setTextColor(valueColor)

  if (
    isDataEnough &&
    optimisticProjection != null &&
    pessimisticProjection != null &&
    optimisticProjection > 0 &&
    pessimisticProjection > 0
  ) {
    const value = `~ ${getProyectionInBlocksByWeeks({ weeks: optimisticProjection })} - ${getProyectionInBlocksByWeeks({ weeks: pessimisticProjection })}`

    doc.setFont("Cairo-SemiBold", "normal")
    doc.setFontSize(12)

    const valueWidth = doc.getTextWidth(value)

    doc.text(value, col2X, col2Y)

    doc.setFontSize(9)
    doc.text("bloques", col2X + valueWidth + 1, col2Y)
  } else {
    doc.text("-", col2X, col2Y)
  }

  //
  // COLUMNA 3
  //

  const col3X = sideMargin + columnWidth * 2
  let col3Y = contentTopY + 13

  doc.setFont("Mulish-Bold", "normal")
  doc.setFontSize(7.5)
  doc.setTextColor(labelColor)
  doc.text("Velocidad promedio", col3X, col3Y)

  col3Y += 6

  doc.setFont("Cairo-Bold", "normal")
  doc.setFontSize(12)
  doc.setTextColor(valueColor)

  if (isDataEnough && averagePoints != null && averagePoints !== 0) {
    const formattedValue = numberFormatter(averagePoints)

    doc.setFont("Cairo-SemiBold", "normal")
    doc.setFontSize(12)

    const valueWidth = doc.getTextWidth(formattedValue)

    doc.text(formattedValue, col3X, col3Y)

    doc.setFontSize(9)
    doc.text(
      `pts dev/${PERIOD_LABELS[period.toLowerCase()] ?? period}`,
      col3X + valueWidth + 1,
      col3Y
    )
    if (showTendency) {
      doc.setFontSize(12)

      if (isPositive) {
        doc.setTextColor(34, 197, 94)
        doc.text("↑", col3X + valueWidth + 18, col3Y)
      } else {
        doc.setTextColor(220, 38, 38)
        doc.text("↓", col3X + valueWidth + 18, col3Y)
      }

      doc.setTextColor(valueColor)
    }
  } else {
    doc.text("-", col3X, col3Y)
  }

  col3Y += 7

  doc.setFont("Mulish-Bold", "normal")
  doc.setFontSize(7.5)
  doc.setTextColor(labelColor)
  doc.text("Fin del alcance estimado", col3X, col3Y)

  col3Y += 6

  doc.setFont("Cairo-SemiBold", "normal")
  doc.setFontSize(12)
  doc.setTextColor(valueColor)

  doc.text(
    isDataEnough && nominalProjectionFormatted ?
      `~ ${nominalProjectionFormatted}`
    : "-",
    col3X,
    col3Y
  )
  //
  // altura uniforme 3 columnas
  //

  const sectionBottom = Math.max(contentTopY + 42, col2Y, col3Y) + 6

  doc.setDrawColor(204, 212, 222)
  doc.setLineWidth(0.3)
  doc.line(sideMargin, sectionBottom, pageWidth - sideMargin, sectionBottom)

  return sectionBottom + 10
}
